import { useCallback, useMemo } from 'react';

import { useFlag } from 'utils/launchdarkly';
import {
  StoreCardButtonOptions,
  defaultStoreCardButtonOptionsOrder,
} from 'utils/launchdarkly/variations';

const convertFlagValueToArray = (flagVariation: Array<string>): StoreCardButtonOptions[] =>
  Object.values(flagVariation) as Array<StoreCardButtonOptions>;

/**
 * @name useStoreCardButtonOptions
 * @description handles converting the raw JSON object from LD into an array of
 * button options & handles additional logic for whether or not they are valid
 */
export default function useStoreCardButtonOptions() {
  const storeCardButtonOptionsFlagValue = useFlag('store-card-button-options');

  const enableOffers = useFlag('enable-offers');

  const filterOptions = useCallback(
    (options: StoreCardButtonOptions[]) => {
      return options.filter((option: StoreCardButtonOptions) => {
        const isValidOption = Object.values(StoreCardButtonOptions).includes(option);

        switch (option) {
          case StoreCardButtonOptions.OFFERS:
            return isValidOption && enableOffers;
          default:
            return isValidOption;
        }
      });
    },
    [enableOffers]
  );

  const storeCardButtonOptions = useMemo(() => {
    const options = storeCardButtonOptionsFlagValue
      ? convertFlagValueToArray(storeCardButtonOptionsFlagValue)
      : defaultStoreCardButtonOptionsOrder;

    return filterOptions(options);
  }, [storeCardButtonOptionsFlagValue, filterOptions]);

  return storeCardButtonOptions;
}
